'use client';

import { useEffect, useState } from 'react';
import { Search, Phone, PhoneIncoming, PhoneOutgoing, Volume2, Loader2, ChevronLeft, ChevronRight, Play, FileText } from 'lucide-react';
import { useNumbers } from '@/hooks/useNumbers';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDateTime, formatDuration, cn } from '@/lib/utils';

export function VoicePage() {
  const { assignments, voiceEvents, loading, fetchAssignments, fetchVoiceEvents, makeCall } = useNumbers();
  const [selectedAssignment, setSelectedAssignment] = useState<string>('');
  const [search, setSearch] = useState('');
  const [directionFilter, setDirectionFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [callDialogOpen, setCallDialogOpen] = useState(false);
  const [toNumber, setToNumber] = useState('');
  const [calling, setCalling] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const eventsPerPage = 20;

  useEffect(() => {
    fetchAssignments();
    if (selectedAssignment) {
      fetchVoiceEvents({ assignmentId: selectedAssignment, limit: eventsPerPage, page: currentPage });
    }
  }, [fetchAssignments, fetchVoiceEvents, selectedAssignment, currentPage]);

  useEffect(() => {
    if (assignments.length > 0 && !selectedAssignment) {
      setSelectedAssignment(assignments[0].id);
    }
  }, [assignments, selectedAssignment]);

  const filteredEvents = voiceEvents.filter(e => {
    if (search && !e.fromNumber.includes(search) && !e.toNumber.includes(search) && !e.callSid?.includes(search)) return false;
    if (directionFilter && e.direction !== directionFilter) return false;
    if (statusFilter && e.status !== statusFilter) return false;
    return true;
  });

  const handleCall = async () => {
    if (!toNumber || !selectedAssignment) return;
    setCalling(true);
    const assignment = assignments.find(a => a.id === selectedAssignment);
    if (assignment) {
      await makeCall({
        to: toNumber,
        from: assignment.phoneNumber,
      });
      setToNumber('');
      setCallDialogOpen(false);
    }
    setCalling(false);
  };

  const getDirectionIcon = (direction: string) => (
    direction === 'inbound' ? (
      <PhoneIncoming className="h-4 w-4 text-green-400" />
    ) : (
      <PhoneOutgoing className="h-4 w-4 text-blue-400" />
    )
  );

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'answered':
        return <Badge className="bg-green-500/10 text-green-400">Answered</Badge>;
      case 'completed':
        return <Badge className="bg-purple-500/10 text-purple-400">Completed</Badge>;
      case 'busy':
        return <Badge className="bg-amber-500/10 text-amber-400">Busy</Badge>;
      case 'no_answer':
        return <Badge className="bg-blue-500/10 text-blue-400">No Answer</Badge>;
      case 'failed':
        return <Badge className="bg-red-500/10 text-red-400">Failed</Badge>;
      case 'canceled':
        return <Badge className="bg-slate-500/10 text-slate-400">Canceled</Badge>;
      case 'ringing':
        return <Badge className="bg-cyan-500/10 text-cyan-400 animate-pulse">Ringing</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <Card className="glass">
          <CardContent className="pt-6">
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map(i => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">Voice Calls</h1>
          <p className="text-slate-400 mt-1">View call history and make calls</p>
        </div>
        <Dialog open={callDialogOpen} onOpenChange={setCallDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Phone className="mr-2 h-4 w-4" />
              Make Call
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Make a Call</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-300">Call</label>
                <Input
                  placeholder="+15551234567"
                  value={toNumber}
                  onChange={e => setToNumber(e.target.value)}
                  className="font-mono"
                />
              </div>
              {selectedAssignment && (
                <p className="text-xs text-slate-500">
                  Calling from {assignments.find(a => a.id === selectedAssignment)?.phoneNumber}
                </p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCallDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCall} disabled={calling || !toNumber}>
                {calling ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : 'Call'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-4 lg:grid-cols-4">
        <Card className="glass lg:col-span-1">
          <CardHeader>
            <CardTitle>Numbers</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {assignments.map(assignment => (
                <Button
                  key={assignment.id}
                  variant={selectedAssignment === assignment.id ? 'default' : 'outline'}
                  className="w-full justify-start gap-3"
                  onClick={() => { setSelectedAssignment(assignment.id); setCurrentPage(1); }}
                >
                  <Phone className={cn('h-4 w-4', selectedAssignment === assignment.id ? 'text-white' : 'text-slate-400')} />
                  <div className="flex-1 text-left">
                    <p className="font-medium text-sm">{assignment.phoneNumber}</p>
                    <p className="text-xs text-slate-400">{assignment.status}</p>
                  </div>
                </Button>
              ))}
              {assignments.length === 0 && (
                <p className="text-center text-slate-500 py-4">No numbers assigned</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="glass lg:col-span-3">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Call History</CardTitle>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <Input
                    placeholder="Search calls..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="pl-10 w-48"
                  />
                </div>
                <Select value={directionFilter} onValueChange={(v) => setDirectionFilter(v)}>
                  <SelectTrigger className="w-32">
                    <SelectValue placeholder="Direction" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">All</SelectItem>
                    <SelectItem value="inbound">Inbound</SelectItem>
                    <SelectItem value="outbound">Outbound</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v)}>
                  <SelectTrigger className="w-36">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">All</SelectItem>
                    <SelectItem value="answered">Answered</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="busy">Busy</SelectItem>
                    <SelectItem value="no_answer">No Answer</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-[600px] overflow-y-auto">
              {filteredEvents.length > 0 ? (
                filteredEvents.map(event => (
                  <div
                    key={event.id}
                    className={cn(
                      'p-4 rounded-xl border transition-all',
                      event.direction === 'inbound'
                        ? 'bg-green-500/5 border-green-500/10'
                        : 'bg-blue-500/5 border-blue-500/10'
                    )}
                  >
                    <div className="flex items-start gap-3">
                      <div className={cn('p-2 rounded-lg', event.direction === 'inbound' ? 'bg-green-500/10' : 'bg-blue-500/10')}>
                        {getDirectionIcon(event.direction)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-mono font-medium text-white">
                              {event.direction === 'inbound' ? event.fromNumber : event.toNumber}
                            </p>
                            {getStatusBadge(event.status)}
                          </div>
                          <p className="text-xs text-slate-500 whitespace-nowrap">
                            {formatDateTime(event.createdAt)}
                          </p>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-slate-400">
                          <span className="flex items-center gap-1">
                            <Volume2 className="h-3 w-3" />
                            {formatDuration(event.durationSeconds)}
                          </span>
                          {event.recordingUrl && (
                            <Button variant="ghost" size="sm" className="text-cyan-400 hover:text-cyan-300 p-1">
                              <Play className="h-4 w-4" />
                            </Button>
                          )}
                          {event.transcription && (
                            <Button variant="ghost" size="sm" className="text-purple-400 hover:text-purple-300 p-1">
                              <FileText className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                        {event.transcription && (
                          <p className="mt-2 text-sm text-slate-300 bg-slate-800/50 p-2 rounded">
                            {event.transcription}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-12 text-slate-400">
                  <Phone className="h-12 w-12 mx-auto mb-4 text-slate-600" />
                  <p>No calls found</p>
                  {selectedAssignment && (
                    <Button variant="outline" className="mt-4" onClick={() => setCallDialogOpen(true)}>
                      <Phone className="mr-2 h-4 w-4" />
                      Make First Call
                    </Button>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-700/50">
              <p className="text-sm text-slate-400">
                Showing {filteredEvents.length} calls
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm text-slate-300">Page {currentPage}</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => p + 1)}
                  disabled={filteredEvents.length < eventsPerPage}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}